/* Copyright (c) 2026 Xperi. All Rights Reserved. */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-syntax */

import { ErrorTypes } from "@tivocorp/data-domain-lib";

import { ErrorCodeTypes } from "../enums/ErrorCodeTypes";
import { IErrorAdapter } from "../errorMapper/adapters/IErrorAdapter";
import { NormalizedError } from "../errorMapper/adapters/NormalizedError";
import { ErrorCodeSuffixMapper } from "./ErrorCodeSuffixMapper";

/**
 * Utility for extracting and formatting error codes from API errors.
 * Uses dependency injection - accepts adapter as parameter.
 */
export class ErrorCodeUtil {
  /**
   * Prefix of a service-location failure code (`A011-*`). Failing to obtain
   * the service location for the endpoint about to be called is the S613
   * condition.
   *
   * Note `TsriServiceEndpointError` sets bare codes (`A013`/`A014`/`A016`/
   * `A017`) rather than prefixed ones, so today only the `TSRI_UNKNOWN_ERROR`
   * type branch below resolves to S613; this prefix check covers any prefixed
   * code a future caller composes.
   *
   * Read on demand rather than into a static field: this class sits under the
   * app store's import graph, so dereferencing another module at import time
   * would turn a stale or partial `data-domain-lib` into a crash on load
   * instead of a missing sub-error code.
   */
  private static getTsriErrorCodePrefix(): string {
    if (ErrorTypes === undefined || ErrorTypes === null) {
      return "";
    }
    const prefix: string | undefined = ErrorTypes.TSRI_SERVICE_FAILURE;
    return typeof prefix === "string" ? prefix : "";
  }

  /**
   * Extracts the sub-error code from an error object.
   * @returns Suffix error code (e.g., 'N603', 'G400') or empty string
   */
  public static extractSubErrorCode(
    error: any,
    adapter: IErrorAdapter
  ): string {
    if (!error) {
      return "";
    }

    const normalizedError = adapter.normalizeError(error);
    const modelErrorCode = this.mapErrorToErrorCodeType(normalizedError);

    if (!modelErrorCode) {
      return "";
    }

    return ErrorCodeSuffixMapper.getSuffixCodeFromErrorType(modelErrorCode);
  }

  /**
   * Gets suffix code from ErrorCodeTypes enum value.
   */
  public static getSuffixFromErrorType(errorCodeType: ErrorCodeTypes): string {
    return ErrorCodeSuffixMapper.getSuffixCodeFromErrorType(errorCodeType);
  }

  /**
   * Maps normalized error to ErrorCodeTypes enum.
   *
   * Applies a priority-based mapping strategy, most specific layer first:
   * 1. Declared classification (error.errorCodeType) - the thrower already
   *    knows the layer (empty body, demarshal failure, …)
   * 2. Service context (AppError type/code) - token, service location,
   *    empty/partial response → S6xx
   * 3. Network errors (error.isNetworkError && error.code) - DNS, connection
   *    failures → N6xx
   * 4. OpenAPI error envelope (error.response.data.code) - the `{ code,
   *    message }` body the service contract defines
   * 5. HTTP status codes (error.response.status) - standard HTTP errors
   *
   * This priority ensures specific error codes take precedence over generic ones.
   *
   * @param error - Normalized error object from IErrorAdapter
   * @returns Mapped ErrorCodeTypes or null if no mapping found
   */
  private static mapErrorToErrorCodeType(
    error: NormalizedError | null | undefined
  ): ErrorCodeTypes | null {
    if (!error) {
      return null;
    }

    // Layer 0: classification declared by the thrower
    if (error.errorCodeType) {
      return error.errorCodeType;
    }

    // Layer 1: service context (auth, service location, response integrity)
    const serviceContextError = this.mapServiceContextToModelError(error);
    if (serviceContextError) {
      return serviceContextError;
    }

    // Layer 2: transport — check for network errors using isNetworkError flag
    if (error.isNetworkError && error.code) {
      const networkErrorCode = this.mapNetworkErrorCodeToModelError(error.code);
      if (networkErrorCode) {
        return networkErrorCode;
      }
    }

    // Extract API error code
    let apiErrorCode = "";
    if (error.response?.data?.code) {
      apiErrorCode = error.response.data.code;
    } else if (error.code && !error.isNetworkError) {
      apiErrorCode = error.code;
    }

    // Extract HTTP status. Adapters put it on `response`, which is the only
    // place a status can reach this method.
    const httpStatus: number =
      error.response?.status === undefined ? 0 : error.response.status;

    // Layer 3: OpenAPI error envelope
    if (apiErrorCode) {
      const modelError = this.mapApiErrorCodeToModelError(
        apiErrorCode,
        httpStatus
      );
      if (modelError) {
        return modelError;
      }
    }

    // Layer 4: fallback to HTTP status mapping
    if (httpStatus > 0) {
      return this.mapHttpStatusToModelError(httpStatus);
    }

    return null;
  }

  /**
   * Maps a service-context failure to ErrorCodeTypes.
   *
   * These are raised before or around the HTTP call rather than by it: the
   * token guard in `DeviceModel`, TSRI service-location lookup, and the
   * `AppError` subclasses services throw for a response that arrived but is
   * unusable. They resolve to the S6xx namespace (plus the two N6xx response
   * integrity codes, which describe the bytes rather than the connection).
   *
   * @param error - Normalized error object from IErrorAdapter
   * @returns Mapped ErrorCodeTypes or null if no mapping exists
   */
  private static mapServiceContextToModelError(
    error: NormalizedError
  ): ErrorCodeTypes | null {
    const typeMap: Record<string, ErrorCodeTypes> = {
      // The generated OpenAPI client rejected the call before sending it —
      // `assertParamExists` found a required parameter missing, so no request
      // was ever formed. Reported as a malformed request (N604).
      RequiredError: ErrorCodeTypes.HTTP_BAD_REQUEST,
      // Response arrived but is unusable
      EmptyResponseError: ErrorCodeTypes.SHORT_RESPONSE,
      PartialResponseError: ErrorCodeTypes.BAD_RESPONSE,
      // JSON.parse / demarshal failure while reading a response
      SyntaxError: ErrorCodeTypes.SCHEMA_EXCEPTION,
      // Service location could not be resolved
      TSRI_UNKNOWN_ERROR: ErrorCodeTypes.SERVER_NOT_RESOLVED
    };

    const codeMap: Record<string, ErrorCodeTypes> = {
      // TokenManager — token present but expired
      "Not Authorised": ErrorCodeTypes.CREDENTIAL_EXPIRED,
      // TokenManager — no token at all
      "Not Authenticated": ErrorCodeTypes.INVALID_ACCESS_TOKEN
    };

    if (error.serviceErrorType && typeMap[error.serviceErrorType]) {
      return typeMap[error.serviceErrorType];
    }

    if (error.serviceErrorCode) {
      if (codeMap[error.serviceErrorCode]) {
        return codeMap[error.serviceErrorCode];
      }
      const tsriPrefix: string = this.getTsriErrorCodePrefix();
      if (
        tsriPrefix.length > 0 &&
        error.serviceErrorCode.startsWith(tsriPrefix)
      ) {
        return ErrorCodeTypes.SERVER_NOT_RESOLVED;
      }
    }

    return null;
  }

  /**
   * Maps network error codes to ErrorCodeTypes.
   *
   * These are low-level network errors from the HTTP client (e.g., Axios),
   * typically found in error.code when no HTTP response is received.
   *
   * Common sources:
   * - DNS resolution failures (ENOTFOUND, EAI_AGAIN)
   * - Connection errors (ECONNREFUSED, ECONNABORTED)
   * - Timeout errors (ETIMEDOUT, ESOCKETTIMEDOUT)
   * - I/O errors (EIO)
   *
   * `ERR_CANCELED` is deliberately absent: an aborted request is an
   * intentional cancellation, not a failure to report.
   *
   * @param networkErrorCode - The network error code string (e.g., 'ENOTFOUND')
   * @returns Mapped ErrorCodeTypes or null if no mapping exists
   */
  private static mapNetworkErrorCodeToModelError(
    networkErrorCode: string
  ): ErrorCodeTypes | null {
    const networkErrorMap: Record<string, ErrorCodeTypes> = {
      // DNS/Connection errors
      ENOTFOUND: ErrorCodeTypes.UNKNOWN_SERVER,
      EAI_AGAIN: ErrorCodeTypes.UNKNOWN_SERVER,
      ECONNREFUSED: ErrorCodeTypes.CONNECTION_REFUSED,
      ECONNABORTED: ErrorCodeTypes.CONNECTION_FAILED,
      ERR_NETWORK: ErrorCodeTypes.CONNECTION_FAILED,
      ENETUNREACH: ErrorCodeTypes.CONNECTION_FAILED,
      EHOSTUNREACH: ErrorCodeTypes.CONNECTION_FAILED,

      // Connection state errors
      ECONNRESET: ErrorCodeTypes.CONNECTION_LOST,
      EPIPE: ErrorCodeTypes.CONNECTION_LOST,

      // Timeout errors
      ETIMEDOUT: ErrorCodeTypes.TIMEOUT,
      ESOCKETTIMEDOUT: ErrorCodeTypes.TIMEOUT,
      ERR_SOCKET_TIMEOUT: ErrorCodeTypes.TIMEOUT,

      // Request/Response errors
      ERR_BAD_REQUEST: ErrorCodeTypes.HTTP_BAD_REQUEST,
      ERR_INVALID_URL: ErrorCodeTypes.HTTP_BAD_REQUEST,
      ERR_BAD_OPTION: ErrorCodeTypes.HTTP_BAD_REQUEST,
      ERR_BAD_OPTION_VALUE: ErrorCodeTypes.HTTP_BAD_REQUEST,
      ERR_BAD_RESPONSE: ErrorCodeTypes.BAD_RESPONSE,

      // I/O errors
      EIO: ErrorCodeTypes.IO,
      ENOENT: ErrorCodeTypes.FILE_NOT_FOUND,

      // Generic errors
      ERR_FR_TOO_MANY_REDIRECTS: ErrorCodeTypes.GENERAL,
      ENOTDIR: ErrorCodeTypes.GENERAL,
      EISDIR: ErrorCodeTypes.GENERAL
    };

    return networkErrorMap[networkErrorCode] || null;
  }

  /**
   * Maps API-specific error codes to ErrorCodeTypes.
   *
   * These are application-level error codes returned by backend services in
   * the `{ code, message }` envelope the OpenAPI contracts define, typically
   * found in error.response.data.code when an HTTP response is received.
   *
   * Envelope-generic codes (`APIGW_ERROR`) are skipped whenever an HTTP status
   * is available: the gateway stamps them on 400, 401, 403, 404, 429, 500 and
   * 504 alike, so honouring them would report every one of those as G502.
   *
   * @param apiErrorCode - The API error code string (e.g., 'purchaseError')
   * @param httpStatus - The status the same response carried, 0 when none
   * @returns Mapped ErrorCodeTypes or null if no mapping exists
   */
  private static mapApiErrorCodeToModelError(
    apiErrorCode: string,
    httpStatus: number = 0
  ): ErrorCodeTypes | null {
    const gatewayEnvelopeCodes: string[] = ["APIGW_ERROR", "apigwError"];
    if (httpStatus > 0 && gatewayEnvelopeCodes.includes(apiErrorCode)) {
      return null;
    }

    const errorMap: Record<string, ErrorCodeTypes> = {
      // VOD/Purchase errors
      purchaseError: ErrorCodeTypes.BAD_REQUEST,
      priceMismatch: ErrorCodeTypes.BAD_REQUEST,
      inSufficientCredit: ErrorCodeTypes.FORBIDDEN,
      offerExpired: ErrorCodeTypes.FORBIDDEN,
      rentalNotAllowed: ErrorCodeTypes.FORBIDDEN,

      // Channel/Streaming errors
      invalidChannel: ErrorCodeTypes.BAD_REQUEST,
      channelNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      serviceUnavailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,
      vodLaunchFailed: ErrorCodeTypes.SERVICE_UNAVAILABLE,
      streamNotAvailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,

      // Authentication/Authorization errors
      unauthorized: ErrorCodeTypes.NOT_AUTHORIZED,
      notAuthenticated: ErrorCodeTypes.CREDENTIAL_NOT_AUTHORIZED,
      forbidden: ErrorCodeTypes.FORBIDDEN,
      accessDenied: ErrorCodeTypes.FORBIDDEN,

      // Generic HTTP errors
      badRequest: ErrorCodeTypes.BAD_REQUEST,
      notFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      internalError: ErrorCodeTypes.INTERNAL_ERROR,
      timeout: ErrorCodeTypes.TIMEOUT,
      conflict: ErrorCodeTypes.CONFLICT,
      tooManyRequests: ErrorCodeTypes.TOO_MANY_REQUESTS,

      // Content errors
      resourceNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      contentNotAvailable: ErrorCodeTypes.SERVICE_UNAVAILABLE,
      unsupportedMediaType: ErrorCodeTypes.UNSUPPORTED_MEDIA_TYPE,

      // Infrastructure/Backend errors
      apigwError: ErrorCodeTypes.BAD_GATEWAY,
      dataNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      deviceInfoNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      partnerInfoNotFound: ErrorCodeTypes.OBJECT_NOT_FOUND,
      dynamoDbError: ErrorCodeTypes.INTERNAL_ERROR,
      unexpectedError: ErrorCodeTypes.INTERNAL_ERROR,
      APIGW_ERROR: ErrorCodeTypes.BAD_GATEWAY,
      BAD_REQUEST: ErrorCodeTypes.BAD_REQUEST,
      INTERNAL_SERVER_ERROR: ErrorCodeTypes.INTERNAL_ERROR,

      // Smart Carousel OpenAPI envelope codes
      RESOURCE_NOT_FOUND: ErrorCodeTypes.OBJECT_NOT_FOUND,
      THROTTLED: ErrorCodeTypes.TOO_MANY_REQUESTS,
      QUOTA_EXCEEDED: ErrorCodeTypes.TOO_MANY_REQUESTS
    };

    return errorMap[apiErrorCode] || null;
  }

  /**
   * Maps HTTP status codes to ErrorCodeTypes.
   *
   * Handles standard HTTP status codes from error.response.status.
   * Used as a fallback when no specific API error code is available.
   *
   * Supported ranges:
   * - 4xx: Client errors (400-409, 415, 429)
   * - 5xx: Server errors (500-504)
   * - Unknown 4xx and 5xx codes default to UNHANDLED_ERROR (G600)
   *
   * G is the current namespace for a status-derived sub-code. The `H4xx`/`H5xx`
   * codes listed as "observed" on older catalog entries are the deprecated
   * predecessor — they map one-to-one onto G and are never emitted. Do not add
   * an H branch here.
   *
   * @param httpStatus - The HTTP status code number (e.g., 404)
   * @returns Mapped ErrorCodeTypes or null if status is not an error code
   */
  private static mapHttpStatusToModelError(
    httpStatus: number
  ): ErrorCodeTypes | null {
    const statusMap: Record<number, ErrorCodeTypes> = {
      400: ErrorCodeTypes.BAD_REQUEST,
      401: ErrorCodeTypes.NOT_AUTHORIZED,
      403: ErrorCodeTypes.FORBIDDEN,
      404: ErrorCodeTypes.OBJECT_NOT_FOUND,
      405: ErrorCodeTypes.NOT_ALLOWED,
      409: ErrorCodeTypes.CONFLICT,
      415: ErrorCodeTypes.UNSUPPORTED_MEDIA_TYPE,
      429: ErrorCodeTypes.TOO_MANY_REQUESTS,
      500: ErrorCodeTypes.INTERNAL_ERROR,
      502: ErrorCodeTypes.BAD_GATEWAY,
      503: ErrorCodeTypes.SERVICE_UNAVAILABLE,
      504: ErrorCodeTypes.SERVER_TIMEOUT
    };

    // Direct mapping
    if (statusMap[httpStatus]) {
      return statusMap[httpStatus];
    }

    // A status outside the mapped set is a response the client does not
    // understand — reported as G600 rather than as a status it is not.
    if (httpStatus >= 400) {
      return ErrorCodeTypes.UNHANDLED_ERROR;
    }

    return null;
  }
}
